import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MarketplaceAppService } from '@galaxy/marketplace-apps/v1';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { FulfillmentOrder, PendingActivationStatus } from '@vendasta/order-fulfillment';
import { EMPTY } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { FulfillmentOrderPipesModule } from '../../pipes/fulfillment-order-pipes.module';
import { PendingActivationApprovalDialogueComponent } from '../pending-approval-approve/pending-activation-approval-dialogue.component';
import { PendingApprovalDeclineComponent } from '../pending-approval-decline/pending-approval-decline.component';
import { PendingApprovalServiceInterface, PendingApprovalServiceInterfaceToken } from '../pending-approvals.interface';
import { IsActivationPendingPipe } from './is-activation-pending.pipe';

@Component({
  selector: 'work-order-pending-approvals-card',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    GalaxyAvatarModule,
    IsActivationPendingPipe,
    FulfillmentOrderPipesModule,
    TranslateModule,
  ],
  templateUrl: './pending-approvals-card.component.html',
  styleUrls: ['./pending-approvals-card.component.scss'],
})
export class PendingApprovalsCardComponent {
  @Input() fulfillmentOrder: FulfillmentOrder;
  @Output() pendingActivationChange = new EventEmitter<string>();

  PendingActivationStatus = PendingActivationStatus;

  constructor(
    private dialog: MatDialog,
    @Inject(PendingApprovalServiceInterfaceToken) private pendingApprovalService: PendingApprovalServiceInterface,
    private snackbarService: SnackbarService,
    private appsService: MarketplaceAppService,
  ) {}

  approve(pendingActivation: string) {
    this.dialog
      .open(PendingActivationApprovalDialogueComponent)
      .afterClosed()
      .pipe(
        switchMap((result) => {
          if (result) {
            return this.pendingApprovalService.resolvePendingActivation(
              pendingActivation,
              this.fulfillmentOrder.appId,
              this.fulfillmentOrder.businessId,
              true,
            );
          }
          return EMPTY;
        }),
      )
      .subscribe(
        () => {
          this.snackbarService.openSuccessSnack('PENDING_APPROVALS_CARD.APPROVE_SUCCESS');
          this.fulfillmentOrder.pendingActivationIds = this.fulfillmentOrder.pendingActivationIds.filter(
            (id) => id !== pendingActivation,
          );
          this.pendingActivationChange.emit(pendingActivation);
        },
        () => {
          this.snackbarService.openErrorSnack('PENDING_APPROVALS_CARD.APPROVE_ERROR');
        },
      );
  }

  decline(pendingActivation: string) {
    this.dialog
      .open(PendingApprovalDeclineComponent)
      .afterClosed()
      .pipe(
        switchMap((rejectionReason) => {
          if (rejectionReason) {
            return this.appsService.getApp(this.fulfillmentOrder.appId).pipe(
              switchMap((app) => {
                // if it's an addon, we need to get the parent app id. Otherwise fallback to the appID stored on the FO
                const appId: string =
                  app?.restrictions?.appRequirement?.rules?.[0]?.appIds?.[0] || this.fulfillmentOrder.appId;
                const addonId: string = this.fulfillmentOrder.appId;

                return this.pendingApprovalService.resolvePendingActivation(
                  pendingActivation,
                  appId,
                  this.fulfillmentOrder.businessId,
                  false,
                  appId !== addonId ? addonId : '',
                  null, // accountId,
                  rejectionReason,
                );
              }),
            );
          }
          return EMPTY;
        }),
      )
      .subscribe(
        () => {
          this.snackbarService.openSuccessSnack('PENDING_APPROVALS_CARD.DECLINE_SUCCESS');
          this.fulfillmentOrder.pendingActivationIds = this.fulfillmentOrder.pendingActivationIds.filter(
            (id) => id !== pendingActivation,
          );
          this.pendingActivationChange.emit(pendingActivation);
        },
        () => {
          this.snackbarService.openErrorSnack('PENDING_APPROVALS_CARD.DECLINE_ERROR');
        },
      );
  }
}
